import { useSupportUnread } from '../api/support';
import { SupportAlerts, alertArabic } from './support/SupportAlerts';
import { useState, type ReactNode } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import type { UserProfile } from '../types/api';
import { useAuth } from '../auth/useAuth';
import { useHealth } from '../api/me';
import { useSignaling } from '../hooks/useSignaling';
import { Avatar } from './Avatar';
import { ImpersonationBanner } from './ImpersonationBanner';
import { ThemeToggle } from './ThemeToggle';
import {
  IconCalendar,
  IconDashboard,
  IconEye,
  IconLayers,
  IconLogout,
  IconMenu,
  IconShield,
  IconSparkle,
  IconUsers,
} from './Icons';

interface NavItem {
  to: string;
  label: string;
  icon: (p: { className?: string }) => ReactNode;
}

const ADMIN_NAV: NavItem[] = [
  { to: '/website', label: 'محتوى الموقع', icon: IconLayers },
  { to: '/support', label: 'User support', icon: IconUsers },
  { to: '/learning-plans', label: 'الخطط التعليمية', icon: IconLayers },
  { to: '/', label: 'Overview', icon: IconDashboard },
  { to: '/users', label: 'People', icon: IconUsers },
  { to: '/tutor-applications', label: 'Applications', icon: IconShield },
  { to: '/sessions', label: 'Lessons', icon: IconCalendar },
  { to: '/trials', label: 'Trials', icon: IconSparkle },
  { to: '/subscriptions', label: 'Subscriptions', icon: IconLayers },
  { to: '/audit', label: 'Audit log', icon: IconShield },
];

export function Layout() {
  const { user, logout, isImpersonating } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const supportUnread = useSupportUnread(user?.role === 'admin' && !isImpersonating);
  const health = useHealth();
  const { connected, supportEvent } = useSignaling(Boolean(user));
  const [railOpen, setRailOpen] = useState(false);

  const nav = isImpersonating
    ? [{ to: '/acting-as', label: 'Acting as', icon: IconEye }]
    : ADMIN_NAV;

  const apiDown = health.isError || health.data?.status === 'degraded';

  return (
    <SupportAlerts userId={user?.id ?? ''} event={supportEvent} text={(key) => alertArabic[key]}
      activeThreadId={location.pathname === '/support' ? new URLSearchParams(location.search).get('studentId') : null}
      openThread={(id) => navigate(`/support?studentId=${id}`)}>
    <div className="flex h-full min-h-screen flex-col bg-paper">
      <ImpersonationBanner />

      <div className="flex min-h-0 flex-1">
        {/* The rail stays dark in both themes — it is chrome, not content,
            and a constant edge makes the content plane read as the page. */}
        <aside
          className={`${
            railOpen ? 'flex' : 'hidden'
          } fixed inset-y-0 left-0 z-30 w-60 shrink-0 flex-col bg-rail text-rail-ink md:static md:flex`}
        >
          <div className="flex h-14 items-center gap-2.5 px-4">
            <span
              aria-hidden="true"
              className="grid size-7 place-items-center rounded bg-rail-hover text-sm"
            >
              🎓
            </span>
            <div className="min-w-0">
              <div className="truncate text-[0.8125rem] font-semibold">Tutoring Platform</div>
              <div className="text-[0.6875rem] text-rail-ink-2">Operations</div>
            </div>
          </div>

          <nav className="flex-1 space-y-0.5 px-2 py-2">
            {nav.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                onClick={() => setRailOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[0.8125rem] transition-colors ${
                    isActive
                      ? 'bg-rail-hover font-medium text-rail-ink'
                      : 'text-rail-ink-2 hover:bg-rail-hover hover:text-rail-ink'
                  }`
                }
              >
                <Icon className="size-[18px]" />
                {label}
                {to === '/support' && (supportUnread.data?.unreadCount ?? 0) > 0 && <span className="ml-auto rounded-full bg-rail-hover px-2 text-xs" role="status" aria-label="Unread support messages">{supportUnread.data?.unreadCount}</span>}
              </NavLink>
            ))}
          </nav>

          {/* Connection state belongs where it is always visible: if the API
              or the socket is down, every number on screen is stale and the
              operator needs to know before they act on one. */}
          <div className="space-y-1.5 border-t border-white/10 px-4 py-3 text-[0.6875rem] text-rail-ink-2">
            <div className="flex items-center gap-1.5">
              <span
                className={`size-1.5 rounded-full ${apiDown ? 'bg-danger' : 'bg-good'}`}
                aria-hidden="true"
              />
              API {apiDown ? 'unreachable' : 'connected'}
            </div>
            <div className="flex items-center gap-1.5">
              <span
                className={`size-1.5 rounded-full ${connected ? 'bg-good' : 'bg-rail-ink-2'}`}
                aria-hidden="true"
              />
              Live updates {connected ? 'on' : 'off'}
            </div>
          </div>
        </aside>

        {railOpen && (
          <button
            type="button"
            aria-label="Close navigation"
            className="fixed inset-0 z-20 bg-black/40 md:hidden"
            onClick={() => setRailOpen(false)}
          />
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-14 shrink-0 items-center gap-3 border-b border-rule bg-surface px-4">
            <button
              type="button"
              className="btn btn-ghost size-8 p-0 md:hidden"
              onClick={() => setRailOpen(true)}
              aria-label="Open navigation"
            >
              <IconMenu />
            </button>

            <div className="ml-auto flex items-center gap-2">
              <ThemeToggle />
              {user && (
                <div className="flex items-center gap-2.5 border-l border-rule pl-3">
                  {/* While impersonating, this chip is the person being acted
                      as rather than the operator, and their account is not
                      the operator's to edit — so it stops being a link. */}
                  {isImpersonating ? (
                    <IdentityChip user={user} />
                  ) : (
                    <NavLink
                      to="/account"
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 rounded-md px-1.5 py-1 transition-colors ${
                          isActive ? 'bg-surface-2' : 'hover:bg-surface-hover'
                        }`
                      }
                      title="Your account"
                    >
                      <IdentityChip user={user} />
                    </NavLink>
                  )}
                  <button
                    type="button"
                    className="btn btn-ghost size-8 p-0"
                    title="Sign out"
                    aria-label="Sign out"
                    onClick={() => {
                      logout();
                      navigate('/login', { replace: true });
                    }}
                  >
                    <IconLogout />
                  </button>
                </div>
              )}
            </div>
          </header>

          <main className="min-h-0 flex-1 overflow-y-auto">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
    </SupportAlerts>
  );
}

/** Who the console thinks you are, in the top-right corner. */
function IdentityChip({ user }: { user: UserProfile }) {
  return (
    <>
      <Avatar name={user.fullName} src={user.avatarUrl} size="sm" />
      <div className="hidden min-w-0 leading-tight sm:block">
        <div className="truncate text-xs font-medium text-ink">{user.fullName}</div>
        <div className="truncate text-[0.6875rem] text-ink-3">{user.email}</div>
      </div>
    </>
  );
}

/** Page frame: title, optional description, optional actions. */
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold tracking-[-0.01em] text-ink">{title}</h1>
        {description && <p className="mt-0.5 text-[0.8125rem] text-ink-3">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Page({ children }: { children: ReactNode }) {
  return <div className="mx-auto w-full max-w-[90rem] px-4 py-5 sm:px-6">{children}</div>;
}
